import { z } from "zod";
import { ParentingStatus, UserInterest } from "../constants/user-profile.enums";
import { paginationSchema } from "../interfaces/pagination";
import { paginationMetaSchema } from "../interfaces/base-api-response";
import { locationCoordinatesSchema } from "../interfaces/location";
import { userAddressResponseSchema } from "./user-address.schema";

/** GET wire may send strings; output matches PATCH / platform enums. */
const platformParentingStatusSchema = z
  .union([z.nativeEnum(ParentingStatus), z.string(), z.null(), z.undefined()])
  .transform((v): ParentingStatus | undefined => {
    if (v == null || v === "") return undefined;
    return Object.values(ParentingStatus).includes(v as ParentingStatus) ? (v as ParentingStatus) : undefined;
  });

const platformUserInterestsSchema = z
  .union([
    z.array(z.union([z.nativeEnum(UserInterest), z.string()])),
    z.string(),
    z.null(),
    z.undefined(),
  ])
  .transform((v): UserInterest[] | undefined => {
    if (v == null || v === "") return undefined;
    const tokens = Array.isArray(v) ? v : v.split(",").map((s) => s.trim()).filter(Boolean);
    const interests = tokens.filter((x): x is UserInterest =>
      Object.values(UserInterest).includes(x as UserInterest),
    );
    return interests.length > 0 ? interests : undefined;
  });

/**
 * Pagination fields reuse {@link paginationSchema} (DRY with subscription-style v4 queries).
 */
export const getUsersQuerySchema = paginationSchema.extend({
  status: z.enum(["ACTIVE", "INACTIVE", "PENDING", "BLOCKED"]).optional(),
  role: z.string().optional(),
  phoneNumber: z.string().optional(),
  name: z.string().optional(),
  email: z.string().optional(),
  search: z.string().optional(),
});

export type GetUsersQuery = z.infer<typeof getUsersQuerySchema>;

export const platformPetCareNotesSchema = z.object({
  comment: z.string().nullish(),
  tags: z.array(z.string()).nullish(),
  comfortWithPeople: z.string().nullish(),
  reactsAroundOtherDogs: z.string().nullish(),
});

const platformPetDobSchema = z.union([
  z.object({
    date: z.number(),
    month: z.number(),
    year: z.number(),
  }),
  z.number(),
]);

export const platformBreedSchema = z.object({
  id: z.number().optional(),
  petType: z.string().optional(),
  name: z.string().optional(),
  code: z.string(),
  isCommon: z.boolean().optional(),
});

export type PlatformBreed = z.output<typeof platformBreedSchema>;

/**
 * Pet payload returned on platform user list/detail responses.
 */
export const platformPetResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  name: z.string(),
  gender: z.string().nullish(),
  dob: platformPetDobSchema.nullish(),
  profilePicUrl: z.string().nullish(),
  breed: platformBreedSchema,
  description: z.string().nullish(),
  walkingCareNotes: platformPetCareNotesSchema.nullish(),
  groomingCareNotes: platformPetCareNotesSchema.nullish(),
  foodCareNotes: platformPetCareNotesSchema.nullish(),
  createdAt: z.number(),
  updatedAt: z.number(),
});

export type PlatformPetResponse = z.output<typeof platformPetResponseSchema>;

export const v4UserPetsListResponseSchema = z.object({
  items: z.array(platformPetResponseSchema),
  meta: paginationMetaSchema.optional(),
});

export type V4UserPetsListResponse = z.output<typeof v4UserPetsListResponseSchema>;

const platformUserLocationSchema = z.object({
  coordinates: locationCoordinatesSchema.optional(),
  address: z.string().optional(),
  pincode: z.string().optional(),
  region: z.string().optional(),
  city: z.string().optional(),
});

/**
 * Kept permissive on optional/third-party fields so minor API drift does not break the UI.
 */
export const platformUserResponseSchema = z.object({
  id: z.string(),
  email: z
    .union([z.string().email(), z.literal(""), z.null()])
    .optional()
    .transform((v) => (v === "" || v === null || v === undefined ? undefined : v)),
  name: z.string().optional(),
  phoneNumber: z.string(),
  photoUrl: z.string().nullish().transform((v) => v ?? undefined),
  bio: z.string().nullish().transform((v) => v ?? undefined),
  location: platformUserLocationSchema.optional(),
  addresses: z.array(userAddressResponseSchema).optional(),
  petProfiles: z.array(platformPetResponseSchema).optional(),
  isOnboarded: z.boolean().optional(),
  onboardingSteps: z.record(z.unknown()).optional(),
  currentStep: z.string().optional(),
  freshDeskId: z.string().optional(),
  juspayCustomerId: z.string().optional(),
  shopifyCustomerId: z.string().optional(),
  source: z.string().nullish().transform((v) => v ?? undefined),
  isExistingUser: z.boolean(),
  createdAt: z.number(),
  updatedAt: z.number(),
  parentingStatus: platformParentingStatusSchema.optional(),
  userInterests: platformUserInterestsSchema.optional(),
});

export type UserResponse = z.output<typeof platformUserResponseSchema>;

export const usersListResponseSchema = z.object({
  items: z.array(platformUserResponseSchema),
  meta: paginationMetaSchema,
});

export type UsersListResponse = z.output<typeof usersListResponseSchema>;

/**
 * At least one field must be present (after parsing).
 */
export const updateV4UserRequestSchema = z
  .object({
    name: z.string().min(1).nullish(),
    email: z.string().email().nullish(),
    bio: z.string().nullish(),
    photoUrl: z.string().nullish(),
    location: platformUserLocationSchema.nullish(),
    shopifyCustomerId: z.string().nullish(),
    juspayCustomerId: z.string().nullish(),
    parentingStatus: z.nativeEnum(ParentingStatus).nullish(),
    userInterests: z.array(z.nativeEnum(UserInterest)).nullish(),
  })
  .refine(
    (data) => Object.values(data).some((v) => v !== undefined),
    { message: "At least one field must be provided for update" },
  );

export type UpdateV4UserRequest = z.infer<typeof updateV4UserRequestSchema>;
export type PetDobSchema = z.infer<typeof platformPetDobSchema>;
export type PlatformPetCareNotes = z.infer<typeof platformPetCareNotesSchema>;
